import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2, Wallet, Clock, ArrowUp, ArrowDown } from "lucide-react";
import AdminShell, { Panel, StatusPill, Btn } from "@/components/admin/AdminShell";
import AdminPager from "@/components/admin/AdminPager";
import KpiCard from "@/components/admin/KpiCard";
import { api } from "@/lib/api";
import { useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const STATUS_TABS = [
  { k: "pending_admin", label: "Pending Approval" },
  { k: "broadcasting",  label: "Broadcasting" },
  { k: "completed",     label: "Completed" },
  { k: "rejected",      label: "Rejected" },
  { k: "cancelled",     label: "Cancelled" },
];

const PILL = {
  pending_admin: "amber", broadcasting: "blue", completed: "green",
  rejected: "red", cancelled: "gray",
};

export default function WithdrawalApprovals() {
  const [tab, setTab] = useState("pending_admin");
  const [rows, setRows] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const pager = useAdminPager();

  const load = async () => {
    const [a, k] = await Promise.all([
      api.get("/api/admin/wallet/withdrawals", { params: { status: tab, skip: pager.skip, limit: pager.limit } }),
      api.get("/api/admin/wallet/kpis"),
    ]);
    const { items, total } = unwrapAdminList(a.data, "withdrawals");
    setRows(items);
    pager.setTotal(total);
    setKpis(k.data);
  };
  useEffect(() => { pager.resetPage(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, pager.page]);

  // Auto-poll so confirming → completed transitions are visible
  useEffect(() => {
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [tab]);

  const approve = async (txId) => {
    setBusy(txId); setError("");
    try {
      await api.post(`/api/admin/wallet/withdrawals/${txId}/approve`, { note: "Approved by ops" });
      load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  const reject = async (txId) => {
    const reason = window.prompt("Rejection reason:");
    if (!reason) return;
    setBusy(txId); setError("");
    try {
      await api.post(`/api/admin/wallet/withdrawals/${txId}/reject`, { reason });
      load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  return (
    <AdminShell title="Withdrawal approvals" subtitle="Crypto withdrawals held for manual review.">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Pending Approval" value={kpis?.pending_withdrawals ?? "—"} icon={Clock} variant="yellow" />
        <KpiCard label="Deposits Today" value={kpis?.deposits_today ?? "—"} icon={ArrowDown} variant="blue" />
        <KpiCard label="Withdrawals Today" value={kpis?.withdrawals_today ?? "—"} icon={ArrowUp} variant="purple" />
        <KpiCard label="Assets Tracked" value={kpis?.balance_by_asset?.length ?? "—"} icon={Wallet} variant="red" />
      </div>

      {/* Status tabs */}
      <Panel className="mb-5">
        <div className="flex flex-wrap gap-2 p-3">
          {STATUS_TABS.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-3 py-1.5 rounded-md text-[12px] font-semibold ${
                tab === t.k ? "bg-warning text-surface" : "text-zinc-300 hover:bg-zinc-800"
              }`}
              data-testid={`approval-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </Panel>

      {error && (
        <div className="mb-3 p-3 rounded-md bg-red-500/10 text-red-300 text-[12px]">{error}</div>
      )}

      <Panel>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="text-zinc-400 text-left border-b border-zinc-800">
              <tr>
                <th className="px-4 py-3">Tx ID</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Asset / Network</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-right">Fee</th>
                <th className="px-4 py-3">Destination</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows === null ? (
                <tr><td colSpan="9" className="px-4 py-12 text-center text-zinc-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="9" className="px-4 py-12 text-center text-zinc-500">No withdrawals in this status.</td></tr>
              ) : rows.map((w) => (
                <tr key={w.tx_id} className="border-b border-zinc-800/50" data-testid={`withdrawal-row-${w.tx_id}`}>
                  <td className="px-4 py-3 font-mono text-zinc-300">{w.tx_id.slice(-12)}</td>
                  <td className="px-4 py-3 text-zinc-300">{w.user_email || w.user_id?.slice(-8)}</td>
                  <td className="px-4 py-3 text-zinc-200">
                    <div className="font-semibold">{w.asset}</div>
                    <div className="text-[11px] text-zinc-500">{w.network}</div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-zinc-200 font-semibold">{w.amount}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-zinc-400">{w.fee}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-zinc-400" title={w.destination_address}>
                    {String(w.destination_address || "").slice(0, 8)}…{String(w.destination_address || "").slice(-6)}
                    {w.memo && <div className="text-amber-400">tag: {w.memo}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill value={
                      w.status === "broadcasting" ? `${w.status} ${w.confirmations || 0}/${w.required_confirmations}` : w.status
                    } />
                  </td>
                  <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">
                    {new Date(w.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {w.status === "pending_admin" && (
                      <div className="inline-flex gap-1">
                        <Btn
                          onClick={() => approve(w.tx_id)}
                          disabled={busy === w.tx_id}
                          kind="success"
                          data-testid={`approve-${w.tx_id}`}
                        >
                          {busy === w.tx_id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve
                        </Btn>
                        <Btn
                          onClick={() => reject(w.tx_id)}
                          disabled={busy === w.tx_id}
                          kind="danger"
                          data-testid={`reject-${w.tx_id}`}
                        >
                          <XCircle size={12} /> Reject
                        </Btn>
                      </div>
                    )}
                    {w.status === "rejected" && w.rejection_reason && (
                      <div className="text-[11px] text-red-300 italic max-w-[160px] truncate" title={w.rejection_reason}>
                        {w.rejection_reason}
                      </div>
                    )}
                    {w.tx_hash && (
                      <div className="font-mono text-[10px] text-zinc-500 truncate max-w-[160px]" title={w.tx_hash}>
                        {String(w.tx_hash).slice(0, 10)}…
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPager
          page={pager.page}
          total={pager.total}
          pageSize={pager.pageSize}
          onPage={pager.setPage}
          testId="withdrawals-pager"
        />
      </Panel>
    </AdminShell>
  );
}
